import { Add } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Button from "../../components/Button";
import ControlChip from "../../components/Chip";
import useStrings from "../../hooks/useStrings";
import DataTable from "../../components/DataTable";
import FormField from "../../components/FormField";
import Typography from "../../components/Typography";
import Autocomplete from "../../components/Autocomplete";
import useContainerGroup from "../../hooks/useContainerGroup";
import GroupService from "../../services/identity/group.service";
import CustomStatusCell from "../../components/CustomStatusCell";
import ModalCreateUpdateGroup from "../../components/Group/modalCreateUpdateGroup";

import { MainComponent } from "../main";
import { Constants } from "../../constants/constants";
import { GroupType, Mode, Status } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { ICodename, ILableOrganization, IPropListContainer } from "../../commons/interfaces";

export interface IRequestGetPagedGroup {
    orderby?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
    searchText?: string;
    listStatus?: string[];
}

interface IModel {
    requestData?: IRequestGetPagedGroup;
    requestDataTemp?: IRequestGetPagedGroup;
}

type IProps = IPropListContainer<IRequestGetPagedGroup> & ILableOrganization & {
    title?: string;
    hidenActionUpdate?: boolean,
    hidenActionDelete?: boolean,
    hidenActionCreate?: boolean,
    hidenInputSelectGroupParent?: boolean;
    requiredInputSelectGroupParent?: boolean;
}

const GroupListContainer = ({
    title,
    lableGroup,

    hidenActionCreate,
    hidenActionDelete,
    hidenActionUpdate,
    hidenInputSelectGroupParent,
    requiredInputSelectGroupParent,

    requestGetPaged,
    onNavigate,
    onGetPaged
}: IProps) => {
    const strings = useStrings();

    const {
        getAllDataGroup,
        checkGeneByGroup,
        handleDeleteGroup,
        handleChangeStatusGroup,
    } = useContainerGroup();

    const {
        clientId,
        language,
        userInfo,
        onError,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [dataRows, setDataRows] = useState<any[]>([]);
    const [model, setModel] = useState<IModel>({} as IModel);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);

    const [openCreateGroup, setOpenCreateGroup] = useState<boolean>(false);
    const [listParentGroup, setListParentGroup] = useState<ICodename[]>([]);
    const [idGroupDefaultCompany, setIdGroupDefaultCompany] = useState<string | undefined>(undefined);

    const listStatus: ICodename[] = useMemo(() => [
        { code: `${Status.Active}`, name: strings.Common.ACTIVE },
        { code: `${Status.Inactive}`, name: strings.Common.IN_ACTIVE },
    ], [strings]);

    const newLableGroup = useMemo(() => (
        (lableGroup || strings.Common.GROUP).toLowerCase()
    ), [lableGroup, strings]);

    useEffect(() => {
        const onStarted = async () => {
            const newReq: IRequestGetPagedGroup = {
                totalCount: requestGetPaged.totalCount || 0,
                pageSize: requestGetPaged.pageSize || model?.requestData?.pageSize,
                pageNumber: requestGetPaged.pageNumber || model?.requestData?.pageNumber,
                searchText: requestGetPaged.searchText || model?.requestData?.searchText,
                listStatus: requestGetPaged.listStatus || model?.requestData?.listStatus,
            };

            await getPaged(newReq);
        };
        onStarted();
    }, [requestGetPaged, userInfo?.userProfile?.organizationId]);

    const getPaged = async (newReq?: IRequestGetPagedGroup) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = newReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;

            const requestData: any = {
                pageSize: pageSize,
                pageNumber: pageNumber,
                listStatus: newReq?.listStatus,
                searchText: newReq?.searchText,
                organizationId: userInfo?.userProfile?.organizationId,
                clientId: clientId,
            };

            const resultGetpaged = await GroupService.getPaged(requestData);

            const newDataRow: any[] = [];
            let idParentGroup: any = undefined;
            [...resultGetpaged.items || []].forEach((item: any) => {
                const checkGeneItem = checkGeneByGroup({
                    gene: item.gene,
                    listGroupSlice: userInfo?.listGroup,
                    roleType: userInfo?.userProfile?.roleType,
                    organizationId: userInfo?.userProfile?.organizationId,
                });

                if ((item.type !== 0) && checkGeneItem) {
                    newDataRow.push({
                        id: item.id,
                        name: item.name,
                        gene: item.gene,
                        groupCode: item.groupCode,
                        description: item.description,
                        parentGroupId: item.parentGroup,
                        parentGroupName: (item.parentGroup === idParentGroup) ? "" : item.parentGroupName,
                        organizationId: item.organizationId,
                        childNumber: item.childNumber || 0,
                        status: item.status,
                    });
                } else {
                    idParentGroup = item.id;
                }

                if ([...item.childs || []].length > 0) {
                    [...item.childs || []].forEach((el: any) => {
                        const checkGeneEl = checkGeneByGroup({
                            gene: el.gene,
                            listGroupSlice: userInfo?.listGroup,
                            roleType: userInfo?.userProfile?.roleType,
                            organizationId: userInfo?.userProfile?.organizationId,
                        });

                        checkGeneEl && newDataRow.push({
                            id: el.id,
                            name: el.name,
                            gene: el.gene,
                            groupCode: el.groupCode,
                            description: el.description,
                            parentGroupId: el.parentGroup,
                            parentGroupName: (el.parentGroup === idParentGroup) ? "" : el.parentGroupName,
                            childNumber: el.childNumber || 0,
                            organizationId: el.organizationId,
                            status: el.status,
                        });
                    });
                }
            });

            let query: string = `?pageNumber=${requestData?.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.organizationCode)) {
                query += `&organizationCode=${requestData?.organizationCode}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.searchText)) {
                query += `&searchText=${requestData?.searchText}`;
            }
            if ([...requestData?.listStatus || []].length > 0) {
                [...requestData?.listStatus || []].forEach(val => {
                    query += `&listStatus=${val}`;
                });
            }

            setDataRows(newDataRow);
            setModel({
                requestData: { ...requestData, totalCount: resultGetpaged.totalCount },
                requestDataTemp: { ...requestData, totalCount: resultGetpaged.totalCount },
            });

            onGetPaged({ query, totalCount: resultGetpaged.totalCount });

        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const RenderFilterForm = () => (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        defaultValue={model.requestDataTemp?.searchText}
                        label={strings.format(strings.Group.SEARCH_TEXT, newLableGroup)}
                        placeholder={strings.format(strings.Group.SEARCH_PLACEHOLDER, newLableGroup)}
                        onBlur={(value) => {
                            setModel(prev => ({
                                ...prev,
                                requestDataTemp: {
                                    ...prev.requestDataTemp,
                                    searchText: value
                                },
                            }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        data={listStatus || []}
                        label={strings.Common.STATUS}
                        defaultValue={model.requestDataTemp?.listStatus}
                        placeholder={strings.Organization.SELECT_STATUS}
                        onChange={(value) => {
                            setModel(prev => ({
                                ...prev,
                                requestDataTemp: {
                                    ...prev.requestDataTemp,
                                    listStatus: value
                                },
                            }));
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );

    const dataColumns = useMemo(() => {
        let newColumns = [
            {
                Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.NAME_GROUP, newLableGroup)),
                accessor: "name",
                Cell: (row: any) => (
                    <Typography
                        color="text"
                        variant="caption"
                        fontWeight="medium"
                    >
                        {row?.row?.original?.groupCode}&nbsp;-&nbsp;
                        {Helpers.renderValueByLanguage({ data: row.value, currentLanguage: language })}
                    </Typography>
                )
            },
            {
                Header: strings.Common.STATUS, accessor: "status",
                Cell: (row: any) => (<CustomStatusCell status={Number(row.value)} />),
            },
        ];

        if (!hidenInputSelectGroupParent) {
            const itemTemp = [
                {
                    Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.GROUP_PARENT, newLableGroup)),
                    accessor: "parentGroupName",
                    Cell: (row: any) => (
                        <Typography
                            color="text"
                            variant="caption"
                            fontWeight="medium"
                        >
                            {("0" === row.row?.original?.parentGroup)
                                ? ""
                                : Helpers.renderValueByLanguage({ data: row.value, currentLanguage: language })
                            }
                        </Typography>
                    )
                },
                {
                    Header: Helpers.capitalizeFirstLetter(strings.format(strings.Group.CHILD_NUMBER, newLableGroup)),
                    accessor: "childNumber",
                    Cell: (row: any) => (
                        <Typography
                            color="text"
                            variant="caption"
                            fontWeight="medium"
                        >
                            {row.value}
                        </Typography>
                    )
                },
            ];

            newColumns.splice(1, 0, ...itemTemp);

        } else {
            const itemTemp = [
                {
                    Header: strings.Organization.DESCRIPTION,
                    accessor: "description",
                    Cell: (row: any) => (
                        <Typography
                            color="text"
                            variant="caption"
                            fontWeight="medium"
                        >
                            {row.value?.value?.[language] || ""}
                        </Typography>
                    )
                },
            ];

            newColumns.splice(1, 0, ...itemTemp);
        }

        return newColumns;

    }, [strings, newLableGroup, language, hidenInputSelectGroupParent])

    const actionList = (row: any): any => {
        return [
            {
                key: "detail",
                iconName: "view",
                title: strings.Common.VIEW_DETAIL,
                callback: (row: any) => { onNavigate({ id: row.id, mode: Mode.View }) },
            },

            !hidenActionUpdate &&
            {
                key: "edit",
                iconName: "edit",
                title: strings.Common.EDIT,
                callback: (row: any) => { onNavigate({ id: row.id, mode: Mode.Update }) },
            },

            !hidenActionDelete &&
            {
                key: "delete",
                iconName: "delete",
                title: strings.Common.DELETE,
                callback: (row: any) => {
                    handleDeleteGroup({
                        lable: newLableGroup,
                        params: {
                            id: row.id,
                            childNumber: Number(row.childNumber),
                        },
                        onCallBack: () => {
                            getPaged(model.requestData);
                        },
                    });
                },
            },

            !hidenActionUpdate &&
            {
                key: "inactive",
                iconName: (Number(row.status) === Status.Active) ? "close" : "check",
                title: (Number(row.status) === Status.Active) ? strings.Common.IN_ACTIVE : strings.Common.ACTIVE,
                callback: (row: any) => {
                    handleChangeStatusGroup({
                        onCallBack: () => {
                            getPaged(model.requestData);
                        },
                        params: {
                            id: row.id,
                            status: Number(row.status),
                            organizationId: userInfo?.userProfile?.organizationId
                        },
                    })
                },
            },
        ];
    };

    const handleOpenModalCreateGroup = async () => {
        try {
            onShowLoading();

            let newParentGroup: ICodename[] = [];
            let idGroupDefault: string | undefined = undefined;

            if (!hidenInputSelectGroupParent) {
                const resultGetAllGroup = await getAllDataGroup({
                    clientId: clientId,
                    includeCompany: true,
                    listStatus: [Status.Active],
                    organizationId: userInfo?.userProfile?.organizationId,
                });

                [...(resultGetAllGroup || [])].forEach((item) => {
                    if (Number(item.detail?.type) === GroupType.Company) {
                        idGroupDefault = item.code as string;
                        setIdGroupDefaultCompany(idGroupDefault);
                    } else {
                        const checkGeneItem = checkGeneByGroup({
                            gene: item.detail?.gene,
                            listGroupSlice: userInfo?.listGroup,
                            roleType: userInfo?.userProfile?.roleType,
                            organizationId: userInfo?.userProfile?.organizationId,
                        });

                        if (checkGeneItem) {
                            const newName = Helpers.renderValueByLanguage({ data: item.name, currentLanguage: language });
                            newParentGroup.push({
                                ...item,
                                name: newName,
                            });
                        }
                    }
                });

                setListParentGroup(newParentGroup);
            }

            setOpenCreateGroup(true);
        } catch (error) {

        } finally {
            onHideLoading();
        }
    }

    return (
        <MainComponent>
            <Grid container spacing={2}>
                {(!hidenActionCreate || !Helpers.isNullOrEmpty(title)) &&
                    <Grid item xs={12}>
                        <Box sx={{
                            gap: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: !Helpers.isNullOrEmpty(title) ? "space-between" : "end",
                        }}>
                            {
                                !Helpers.isNullOrEmpty(title) &&
                                <Typography variant="h5">{title}</Typography>
                            }
                            {
                                !hidenActionCreate &&
                                <Button onClick={() => { handleOpenModalCreateGroup() }}>
                                    <Add /> &nbsp; {strings.Common.ADD_NEW}
                                </Button>
                            }
                        </Box>
                    </Grid>
                }

                <Grid item xs={12}>
                    {[...model.requestData?.listStatus || []].length > 0 && (
                        <Box m={2}>
                            {[...model.requestData?.listStatus || []].map((str) => (
                                <ControlChip
                                    label={strings.Common.STATUS}
                                    value={listStatus.find((el) => el.code === str)?.name}
                                    onDelete={() => {
                                        const listTemp = [...model.requestData?.listStatus || []].filter(i => i !== str);
                                        getPaged({ ...model.requestData, listStatus: listTemp });
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    <Card>
                        <Box p={2}>
                            <DataTable
                                loading={loadingDataTable}
                                totalCount={dataRows.length}
                                pageNumber={model.requestData?.pageNumber || 1}
                                rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                                onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                                onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}
                                //
                                searchText={model.requestData?.searchText}
                                placeholderSearch={strings.format(strings.Group.SEARCH_TEXT, newLableGroup)}
                                onSearch={(val) => getPaged({ ...model.requestData, pageNumber: 1, searchText: val })}
                                onSearchChange={(val) => {
                                    setModel(prev => ({
                                        ...prev,
                                        requestData: {
                                            ...prev.requestData,
                                            searchText: val,
                                        }
                                    }));
                                }}
                                // 
                                filterForm={<RenderFilterForm />}
                                onFilter={() => {
                                    getPaged({ ...model.requestDataTemp, pageNumber: 1 });
                                }}
                                onReset={() => {
                                    getPaged({
                                        pageNumber: 1,
                                        searchText: "",
                                        listStatus: [],
                                    });
                                }}
                                onCloseFilter={() => {
                                    setModel(prev => ({ ...prev, requestDataTemp: prev.requestData }));
                                }}
                                //
                                actionList={actionList}
                                table={{
                                    rows: dataRows,
                                    columns: dataColumns,
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Modal Create Group */}
            {openCreateGroup &&
                <ModalCreateUpdateGroup
                    mode={Mode.Create}
                    lable={newLableGroup}
                    openModal={openCreateGroup}
                    organizationId={userInfo?.userProfile?.organizationId}

                    listParentGroup={listParentGroup}
                    groupIdDefault={idGroupDefaultCompany}
                    hidenInputSelectGroupParent={hidenInputSelectGroupParent}
                    requiredInputSelectGroupParent={requiredInputSelectGroupParent}

                    onCloseModal={() => {
                        setOpenCreateGroup(false);
                    }}
                    onActionModal={(newdata) => {
                        setOpenCreateGroup(false);
                        getPaged(model.requestData);
                    }}
                />
            }
        </MainComponent>
    );
};

export default GroupListContainer;
